import type { Holding } from '@interfaces/stats';
import { useMemo } from 'react';

export type HoldingFilterOptions = {
  includeCash?: boolean;
  excludeZeroValue?: boolean;
};

/**
 * Custom hook for filtering holdings based on various criteria
 * @param holdings - Array of holdings to filter
 * @param options - Filtering options
 * @returns Filtered array of holdings
 */
export const useHoldingFilter = (
  holdings: Holding[],
  options?: HoldingFilterOptions,
): Holding[] => {
  return useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return [];
    }

    // Determine if cash holdings should be included
    const shouldIncludeCash = options?.includeCash ?? false;
    const shouldExcludeZeroValue = options?.excludeZeroValue ?? false;

    // Filter holdings based on cash inclusion
    return holdings.filter((holding) => {
      if (shouldExcludeZeroValue && holding.totalCents === 0) {
        return false;
      }
      // If financialProduct is missing, treat as non-cash
      if (!holding?.financialProduct) {
        return true;
      }
      return shouldIncludeCash || holding.financialProduct.isCash !== true;
    });
  }, [holdings, options?.includeCash, options?.excludeZeroValue]);
};
